import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Bot,
  Send,
  Loader2,
  Sparkles,
  ChevronRight,
  User as UserIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const STARTERS = [
  "What are this candidate's biggest strengths?",
  "Where did they show the most concern?",
  "Would they be a good fit for a senior role?",
  "Give me 3 follow-up questions to ask in round 2.",
];

const CONF_STYLE = {
  high: "border-emerald-200 bg-emerald-50 text-emerald-700",
  medium: "border-amber-200 bg-amber-50 text-amber-700",
  low: "border-rose-200 bg-rose-50 text-rose-700",
};

export default function AICopilotChat({ interviewId }) {
  const [turns, setTurns] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [bootstrapped, setBootstrapped] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    if (!interviewId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(
          `/interviews/${interviewId}/copilot/history`
        );
        if (cancelled) return;
        setTurns(data?.turns || []);
      } catch {
        // history is best-effort
      } finally {
        if (!cancelled) setBootstrapped(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [interviewId]);

  useEffect(() => {
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [turns, loading]);

  const ask = async (question) => {
    const q = (question || "").trim();
    if (!q) return;
    if (loading) return;
    setLoading(true);
    setInput("");
    const optimistic = { question: q, pending: true };
    setTurns((prev) => [...prev, optimistic]);
    try {
      const history = turns.flatMap((t) => [
        { role: "user", content: t.question },
        { role: "assistant", content: t.answer || "" },
      ]);
      const { data } = await api.post(
        `/interviews/${interviewId}/copilot/ask`,
        { question: q, history }
      );
      setTurns((prev) => {
        const copy = prev.slice(0, -1);
        copy.push({
          question: q,
          answer: data.answer,
          confidence: data.confidence,
          citations: data.citations || [],
          suggested_followup: data.suggested_followup || "",
        });
        return copy;
      });
    } catch (e) {
      setTurns((prev) => prev.slice(0, -1));
      toast.error(
        e?.response?.data?.detail || "Co-pilot is busy — try again"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    ask(input);
  };

  return (
    <section
      data-testid="ai-copilot-panel"
      className="mt-10 border border-violet-200 bg-white"
    >
      <div className="flex items-center justify-between border-b border-violet-100 bg-gradient-to-r from-violet-50 to-indigo-50 px-5 py-3">
        <div className="flex items-center gap-2">
          <div className="grid h-7 w-7 place-items-center rounded-full bg-violet-600 text-white">
            <Bot className="h-4 w-4" />
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-violet-700">
              AI Co-pilot
            </div>
            <div className="text-[11px] text-slate-500">
              Ask anything — grounded in this candidate&apos;s actual interview
            </div>
          </div>
        </div>
        {turns.length > 0 && (
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
            {turns.length} question{turns.length === 1 ? "" : "s"}
          </div>
        )}
      </div>

      <div
        data-testid="ai-copilot-thread"
        className="max-h-[440px] space-y-4 overflow-y-auto px-5 py-4"
      >
        {bootstrapped && turns.length === 0 && !loading && (
          <div className="space-y-3">
            <div className="flex items-start gap-2 text-xs text-slate-500">
              <Sparkles className="mt-0.5 h-3.5 w-3.5 text-violet-500" />
              <span>
                Try a starter question or type your own. The co-pilot reads
                the candidate&apos;s résumé and every interview transcript.
              </span>
            </div>
            <div className="grid gap-2 sm:grid-cols-2">
              {STARTERS.map((s, i) => (
                <button
                  key={i}
                  data-testid={`ai-copilot-starter-${i}`}
                  onClick={() => ask(s)}
                  className="group flex items-center justify-between gap-2 border border-slate-200 bg-slate-50 px-3 py-2 text-left text-xs font-semibold text-slate-700 hover:border-violet-300 hover:bg-violet-50 hover:text-violet-800"
                >
                  <span>{s}</span>
                  <ChevronRight className="h-3.5 w-3.5 shrink-0 text-slate-400 group-hover:text-violet-500" />
                </button>
              ))}
            </div>
          </div>
        )}

        {turns.map((t, i) => (
          <div key={i} className="space-y-2" data-testid={`ai-copilot-turn-${i}`}>
            {/* User question */}
            <div className="flex items-start gap-2">
              <div className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-slate-200 text-slate-700">
                <UserIcon className="h-3 w-3" />
              </div>
              <div className="flex-1 border-l-2 border-slate-300 bg-slate-50 px-3 py-2 text-sm text-slate-800">
                {t.question}
              </div>
            </div>
            {/* AI answer */}
            {t.pending ? (
              <div className="flex items-start gap-2">
                <div className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-violet-600 text-white">
                  <Bot className="h-3 w-3" />
                </div>
                <div className="flex-1 border-l-2 border-violet-300 bg-violet-50 px-3 py-2 text-sm text-violet-800">
                  <Loader2 className="inline h-4 w-4 animate-spin" />{" "}
                  <span className="ml-1">Thinking…</span>
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-2">
                <div className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-violet-600 text-white">
                  <Bot className="h-3 w-3" />
                </div>
                <div className="flex-1 border-l-2 border-violet-300 bg-violet-50 px-3 py-2">
                  <div className="whitespace-pre-wrap text-sm text-slate-800">
                    {t.answer}
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-1.5">
                    {t.confidence && (
                      <span
                        className={`inline-flex items-center border px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
                          CONF_STYLE[t.confidence] || CONF_STYLE.medium
                        }`}
                        data-testid={`ai-copilot-confidence-${i}`}
                      >
                        {t.confidence} confidence
                      </span>
                    )}
                    {(t.citations || []).map((c, j) => (
                      <span
                        key={j}
                        className="inline-flex items-center border border-blue-200 bg-blue-50 px-1.5 py-0.5 text-[10px] font-semibold text-blue-700"
                      >
                        {c}
                      </span>
                    ))}
                  </div>
                  {t.suggested_followup && (
                    <button
                      onClick={() => ask(t.suggested_followup)}
                      data-testid={`ai-copilot-followup-${i}`}
                      className="mt-2 inline-flex items-center gap-1 border border-violet-300 bg-white px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-violet-700 hover:bg-violet-50"
                    >
                      Ask in round 2: {t.suggested_followup.slice(0, 80)}
                      {t.suggested_followup.length > 80 ? "…" : ""}
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
        <div ref={bottomRef} />
      </div>

      <form
        onSubmit={handleSubmit}
        className="flex items-center gap-2 border-t border-slate-100 bg-slate-50 px-5 py-3"
      >
        <input
          data-testid="ai-copilot-input"
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Ask about this candidate…"
          disabled={loading}
          maxLength={500}
          className="flex-1 border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-violet-500"
        />
        <Button
          type="submit"
          data-testid="ai-copilot-send"
          disabled={loading || !input.trim()}
          className="bg-violet-600 text-white hover:bg-violet-500"
        >
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Send className="h-4 w-4" />
          )}
        </Button>
      </form>
    </section>
  );
}
